/**
 * buildOwnerOptions — shared chronicle ownership-picker logic (DEV-1340).
 *
 * Mirrors the Full-Project owner picker (ProjectConfigModal `eligibleOwnerCommunities`):
 * you can own a chronicle as yourself, or on behalf of an ORGANIZATION where you
 * hold a qualifying role (owner / admin / content_creator) — directly OR inherited
 * from an ancestor org. System admins may own on behalf of any active organization.
 *
 * Org-family only (entity_type 'organization' or 'org_*'); geo_* communities are
 * NOT owner candidates, matching the project picker.
 *
 * On-behalf-of USERS (proxy beneficiaries) are appended by the caller after an
 * async fetch — they are not derivable from these arrays.
 */

export interface OwnerOption {
  value: string;
  label: string;
  type: 'user' | 'community';
}

function isOrgFamily(entity: any): boolean {
  const et = String(entity?.entity_type ?? entity?.entityType ?? '').toLowerCase();
  return et === 'organization' || et.startsWith('org_');
}

export function buildOwnerOptions(
  currentUserGuid: string,
  communityEntities: any[],
  communityRoleAssignments: any[],
  communityEntityRelationships: any[],
  myselfLabel: string,
  isSystemAdmin = false,
): OwnerOption[] {
  const allowedRoles = new Set(['content_creator', 'admin', 'owner']);
  const options: OwnerOption[] = [
    { value: currentUserGuid, label: myselfLabel, type: 'user' },
  ];

  // ── Index every active org entity by GUID ──
  const orgByGuid: Record<string, any> = {};
  if (Array.isArray(communityEntities)) {
    for (const e of communityEntities) {
      const g = String(e?.entity_guid ?? e?.entityGuid ?? '').toUpperCase();
      if (!g) continue;
      if (e?.is_active === false || e?.isActive === false) continue;
      if (!isOrgFamily(e)) continue;
      orgByGuid[g] = e;
    }
  }

  // ── Parent chain (child → parents) for inherited authority ──
  const parentsByChild: Record<string, string[]> = {};
  if (Array.isArray(communityEntityRelationships)) {
    for (const r of communityEntityRelationships) {
      const child = String(r?.child_entity_guid ?? '').toUpperCase();
      const parent = String(r?.parent_entity_guid ?? '').toUpperCase();
      if (!child || !parent) continue;
      (parentsByChild[child] ??= []).push(parent);
    }
  }

  // ── Direct role grants for the current user ──
  const directRoleCommunities = new Set<string>();
  if (Array.isArray(communityRoleAssignments)) {
    for (const ra of communityRoleAssignments) {
      const userGuid = String(ra?.user_guid ?? '').toUpperCase();
      const commGuid = String(ra?.community_entity_guid ?? '').toUpperCase();
      const role = String(ra?.role ?? '').toLowerCase();
      if (!userGuid || !commGuid) continue;
      if (userGuid !== currentUserGuid.toUpperCase()) continue;
      if (!allowedRoles.has(role)) continue;
      directRoleCommunities.add(commGuid);
    }
  }

  const hasInheritedAuthority = (rootGuid: string): boolean => {
    const seen = new Set<string>();
    const stack = [rootGuid.toUpperCase()];
    while (stack.length) {
      const cur = stack.pop()!;
      if (seen.has(cur)) continue;
      seen.add(cur);
      if (directRoleCommunities.has(cur)) return true;
      for (const p of parentsByChild[cur] ?? []) stack.push(p);
    }
    return false;
  };

  for (const g of Object.keys(orgByGuid)) {
    const eligible = isSystemAdmin || hasInheritedAuthority(g);
    if (!eligible) continue;
    const ent = orgByGuid[g];
    const label = String(ent?.name ?? '').trim();
    if (!label) continue;
    options.push({ value: String(ent.entity_guid ?? ent.entityGuid), label, type: 'community' });
  }

  options.sort((a, b) => {
    if (a.type === 'user') return -1;
    if (b.type === 'user') return 1;
    return a.label.localeCompare(b.label);
  });

  return options;
}
